import { formatDateTimeForInputField, formatDateTimeForDisplay } from '../../utils/exam-utilities'
import { Box, Typography, Button, CircularProgress, useTheme, Chip, IconButton, Paper, Tooltip } from '@mui/material'
import QuestionImportFileInputDialog from '../../components/exams-components/QuestionImportFileInputDialog'
import QuestionsPreviewDialog from '../../components/exams-components/QuestionsPreviewDialog'
import DataLoadingSpinner from '../../components/common/DataLoadingSpinner'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import ConfirmDialog from '../../components/common/ConfirmDialog'
import examNotFoundImg from '../../assets/exam-notfound.svg'
import { Link, useNavigate, useParams } from 'react-router-dom'
import BlockMessage from '../../components/common/BlockMessage'
import PublishIcon from '@mui/icons-material/Publish'
import { useUserStore } from '../../store/user.store'
import { useExamStore } from '../../store/exam.store'
import GetAppIcon from '@mui/icons-material/GetApp'
import { EditableMathField } from 'react-mathquill'
import React, { useEffect, useState } from 'react'
import { Icons, FLAT_COLORS_WITH_BACKGROUND, isMobileDevice, latexDelimiters, getGroupChipLabel, getTransformedGroupDetails } from '../../utils/utilities'
import { toast } from 'react-toastify'
import { observer } from 'mobx-react'
import api from '../../service/api'
import { useSubjectStore } from '../../store/subject.store'
import BreadcrumbList from '../../components/common/BreadcrumbList'
import ExamRescheduleDialog from '../../components/exams-components/ExamRescheduleDialog'
import { AccessTime } from '@mui/icons-material'
import { useSettingsStore } from '../../store/settings.store'
import Latex from 'react-latex-next'
import EditQuestionDialog from '../../components/exams-components/EditQuestionDialog'
import CustomTabs from '../../components/common/CustomTabs'
import CustomChip from '../../components/common/CustomChip'
import CountCard from '../../components/common/CountCard'
import dayjs from 'dayjs'

const facultyTabs = [
    { label: "Topics and Questions", value: 'questions', icon: Icons.default.QuestionMark },
    { label: "Assigned Batches and Sections", value: 'topicsandbatches', icon: Icons.default.Group },
]

const Question = ( { editable = false, question, index, fetchQuestions, setQuestionSelectedForEdit } ) => {

    const [deleting, setDeleting] = useState( false )

    const { palette, border, layout } = useTheme()
    const { id } = useParams()

    const deleteQuestion = async () => {
        setDeleting( true )
        try {
            const res = await api.deleteQuestion( id, question.question_id )
            await fetchQuestions()
            toast( res.data.message )
        } catch ( e ) {
            toast( e?.response?.data?.message || "Something went wrong while trying to delete the question." )
        } finally {
            setDeleting( false )
        }
    }

    return (
        <Box sx={{ background: palette.common.bg, border: border[1] }} overflow="hidden" borderRadius={layout.borderRadius} padding="10px" display="flex" gap="10px" alignItems="flex-start" key={question.question_id}>
            <Box flexGrow={1} sx={{ fontSize: "14px", borderRadius: "5px", "& p": { margin: "0 " } }} >
                <Typography variant='subtitle2' fontSize="10px" > <em>Question {index + 1}</em> </Typography>
                <Box>
                    <Box fontWeight="500" fontSize="12px" overflow="clip" sx={{ "& img": { maxWidth: "100%", objectFit: "cover" }, }} color={palette.shadedText}>
                        {question.question_text.trim().includes( 'LATEX-' ) ?
                            <EditableMathField latex={question.question_text.trim().split( "LATEX-" )[1]} id='static-latex' style={{ fontSize: "12px", padding: "0px", fontFamily: "'Roboto' sans-serif !important", fontWeight: "bold", color: palette.shadedText }}> </EditableMathField> :
                            <Latex delimiters={latexDelimiters}>{question.question_text.trim()}</Latex>
                        }
                    </Box>
                    {/* <Box display="inline-flex" color={palette.shadedText} marginTop="5px">
                        <Chip size='small' sx={{ fontSize: "10px", marginRight: "5px", borderRadius: "5px", border: `1px solid ${FLAT_COLORS_WITH_BACKGROUND[2].color}`, color: FLAT_COLORS_WITH_BACKGROUND[2].color, bgcolor: FLAT_COLORS_WITH_BACKGROUND[2].bg }} label={question.question_type}></Chip>
                        {question.question_type === 'MCQ' && <Chip size='small' sx={{ fontSize: "10px", marginRight: "5px", borderRadius: "5px", border: `1px solid ${FLAT_COLORS_WITH_BACKGROUND[2].color}`, color: FLAT_COLORS_WITH_BACKGROUND[2].color, bgcolor: FLAT_COLORS_WITH_BACKGROUND[2].bg }} label={question.question_answer_selection}></Chip>}
                    </Box> */}
                </Box>
            </Box>
            {editable && <Box display="flex" gap="10px">
                <IconButton size='small' onClick={() => setQuestionSelectedForEdit( question )} color="greyed" >{Icons.default.EditIcon}</IconButton>
                <IconButton size='small' disabled={deleting} color="greyed" sx={{ ":hover": { color: "error.main" } }} onClick={deleteQuestion} >{deleting ? <CircularProgress size={16} /> : Icons.default.DeleteIconOutlined}</IconButton>
            </Box>}
        </Box>
    )
}

const Exam = observer( ( { fromSubject = false } ) => {
    let { id } = useParams()
    const [batches, setBatches] = useState( null )
    const [tabValue, setTabValue] = useState( 'questions' )
    const [inProgress, setInProgress] = useState( true )
    const [deleting, setDeleting] = useState( false )
    const [questions, setQuestions] = useState( null )
    const [isImporting, setIsImporting] = useState( false )
    const [notFound, setNotFound] = useState( false )
    const [rescheduleDialogState, setRescheduleDialogState] = useState( false )
    const [deleteDialogState, setDeleteDialogState] = useState( false )
    const [questionsPreview, setQuestionsPreview] = useState( null )
    const [importDialogStatus, setImportDialogStatus] = useState( false )
    const [questionSelectedForEdit, setQuestionSelectedForEdit] = useState( null )
    const [publishing, setPublishing] = useState( false )
    const [accessDenied, setAccessDenied] = useState( false )
    const [attempts, setAttempts] = useState( 0 )
    const [attempting, setAttempting] = useState( false )

    const navigate = useNavigate()
    const UserStore = useUserStore()
    const ExamStore = useExamStore()
    const SubjectStore = useSubjectStore()
    const SettingsStore = useSettingsStore()

    const { palette, border, layout } = useTheme()

    const navigateToExamsPage = () => {
        navigate( UserStore.getUser['user_role'] === "STUDENT" ? "/student/exams" : "/faculty/exams" )
    }

    const navigateOrOpen = url => {
        if ( fromSubject )
            window.open( url )
        else
            navigate( url )
    }

    const getExam = async () => {
        try {
            const { success, code } = await ExamStore.fetchExam( id )
            if ( !success ) {
                if ( code === 404 )
                    return setNotFound( true )
                else if ( code === 403 || code === 401 ) {
                    return setAccessDenied( true )
                }
            }

        } catch ( e ) {
            if ( e.response && e.response.data ) {
                if ( e.response.status === 404 && e.response.data === "Exam not found!" ) {
                    setNotFound( true )
                } else if ( e.response.status === 401 && e.response.status === 403 ) {
                    setAccessDenied( true )
                } else
                    toast.error( e?.response?.data || "Something went wrong while importing questions" )
            }
            else toast.error( "Something went wrong!" )
            navigateToExamsPage()
        } finally {
            setInProgress( false )
        }
    }

    const publish = async () => {
        try {
            setPublishing( true )
            await api.publish( ExamStore.getExam.exam.exam_id )
            await getExam()
            toast.success( "Exam published!" )
        } catch ( e ) {
            console.log( e )
            toast.error( e?.response?.data?.message || "Something went wrong!" )
        } finally {
            setPublishing( false )
        }
    }

    const deleteExam = async () => {
        try {
            setDeleting( true )
            await api.deleteExam( ExamStore.getExam.exam.exam_id )
            await ExamStore.fetchExams()
            toast.success( <div><span style={{ color: palette.customThemeColor.main }}>{ExamStore.getExam.exam.exam_name}</span> successfully deleted</div> )
            navigate( "/faculty/exams" )
        } catch ( e ) {
            toast( e?.response?.data?.message || "Something went wrong! try again later" )
        } finally {
            setDeleteDialogState( false )
            setDeleting( false )
        }
    }

    const attempt = async () => {
        try {
            setAttempting( true )
            const { isMobile } = isMobileDevice()
            if ( isMobile && SettingsStore?.getUseSettings['restrict_exam_attempt_on_mobile_devices'] === true ) {
                toast( `This exam cannot be taken on mobile device! please use laptop or desktop with stable internet connection.` )
                return
            }

            const { status, data } = await api.attempt( ExamStore.getExam.exam.exam_id )
            if ( status === 200 ) {
                if ( !data.isNewParticipation ) toast.success( "Proceeding with the previously unfinished exam! finish this to start new attampt" )
                const url = `/student/exams/${ExamStore.getExam.exam.exam_id}/attempt/${data.attempt}/${data.participation_id}`
                if ( fromSubject )
                    window.open( url )
                else
                    navigate( url )
            }
        } catch ( e ) {
            if ( e?.response?.status === 401 || e?.response?.data?.unauth ) {
                UserStore.setERPSessionState( "ended" )
            }
            else
                toast( e?.response?.data?.message || "Something went wrong" )

        } finally {
            setAttempting( false )
        }
    }

    const getQuestions = async () => {
        try {
            const { data: questionsRes } = await api.fetchQuestions( id, { forediting: true } )
            setQuestions( questionsRes.data || questionsRes )
        } catch ( err ) {
            toast( err?.response?.data?.message || "Something went wrong" )
        }
    }


    useEffect( () => {
        const getExam = async () => {
            try {
                const requests = []
                requests.push( ExamStore.fetchExam( id ) )
                if ( UserStore.getUser["user_role"] === 'FACULTY' || UserStore.getUser["user_role"] === 'HOD' || UserStore.getUser["user_role"] === 'PRINCIPAL' || UserStore.getUser["user_role"] === 'presenter' ) {
                    requests.push( api.fetchExamParticipation( id ) )
                    requests.push( api.fetchQuestions( id, { forediting: true } ) )
                }

                const [examRes, participationRes, questionsRes] = await Promise.all( requests )
                const { success, code } = examRes

                if ( UserStore.getUser.user_role !== "STUDENT" && UserStore.getErrorsStatuses?.academicYearsAndBatches === true )
                    await UserStore.fetchUsersAllBatchesAndSectionList()
                if ( !success ) {
                    if ( code === 404 )
                        return setNotFound( true )
                    else if ( code === 403 || code === 401 ) {
                        return setAccessDenied( true )
                    }
                    else
                        toast.error( "Something went wrong!" )
                } else {
                    if ( UserStore.getUser["user_role"] === 'FACULTY' || UserStore.getUser["user_role"] === 'HOD' || UserStore.getUser["user_role"] === 'PRINCIPAL' || UserStore.getUser["user_role"] === 'presenter' ) {
                        if ( UserStore.getErrorsStatuses?.academicYearsAndBatches === true )
                            await UserStore.fetchUsersAllBatchesAndSectionList()

                        const { data: { participations } } = participationRes
                        const { data: questionsData } = questionsRes

                        setQuestions( questionsData.data || questionsData )
                        setAttempts( participations?.length || 0 )
                    }
                    setBatches( ExamStore.getExam.batches )
                }
            } catch ( e ) {
                console.log( e.response )
                if ( e.response && e.response.data ) {
                    if ( e.response.status === 404 && e.response.data === "Exam not found!" ) {
                        setNotFound( true )
                    } else if ( e.response.status === 403 ) {
                        setAccessDenied( true )
                    } else
                        toast.error( e?.response?.data?.message || "Something went wrong while importing questions" )
                }
                else toast.error( "Something went wrong!" )
                // navigate( '/exams' )
            } finally {
                setInProgress( false )
            }
        }
        getExam()
        return () => {
            setInProgress( true )
        }
    }, [ExamStore, UserStore, id, navigate] )

    const published = ExamStore.getExam?.exam?.exam_status === 'Published'
    const ended = ExamStore.getExam?.exam ? dayjs( `${dayjs( ExamStore.getExam?.exam?.exam_end_date ).format( 'YYYY-MM-DD' )} ${ExamStore.getExam?.exam?.exam_end_time}` ).isBefore( dayjs() ) : false
    const started = ExamStore.getExam?.exam ? dayjs( `${dayjs( ExamStore.getExam?.exam?.exam_start_date ).format( 'YYYY-MM-DD' )} ${ExamStore.getExam?.exam?.exam_start_time}` ).isBefore( dayjs() ) : false

    return (
        !notFound ? <Box flexGrow={1} minWidth="0" display={{ md: "flex", xs: "block" }} flexDirection="column" overflow="auto" padding={layout.pagePadding}>
            {accessDenied && <BlockMessage blockTitle='Access Denied' actions={[{ label: "Back to Exams", icon: Icons.default.ArrowBack, action: () => navigate( UserStore.getUser['user_role'] === "STUDENT" ? "/student/exams" : "/faculty/exams" ) }]} type='error' message='You do not have permission to view this exam information. Only exam creator and admin can access a particular exam.' />}
            {!inProgress && ExamStore.getExam.exam && <ExamRescheduleDialog state={rescheduleDialogState} setState={setRescheduleDialogState} exam={ExamStore.getExam.exam} />}
            <QuestionImportFileInputDialog setQuestionsPreview={setQuestionsPreview} questionsPreview={questionsPreview} isImporting={isImporting} importDialogStatus={importDialogStatus} setImportDialogStatus={setImportDialogStatus} />
            <QuestionsPreviewDialog setIsImporting={setIsImporting} getQuestions={getQuestions} examId={id} setQuestionsPreview={setQuestionsPreview} questionsPreview={questionsPreview} />
            <ConfirmDialog waiting={deleting} status={deleteDialogState} confirmAction={deleteExam} cancelAction={() => setDeleteDialogState( false )} actionName="Yes, Delete It" message={<p>The exam <strong><em>{ExamStore.getExam?.exam?.exam_name}</em></strong> will be deleted along with it's question details.</p>} />
            {Boolean( questionSelectedForEdit ) && <EditQuestionDialog module='exam' getQuestions={getQuestions} state={Boolean( questionSelectedForEdit )} setState={() => setQuestionSelectedForEdit( null )} question={questionSelectedForEdit} />}

            {!fromSubject && !accessDenied && !notFound && <BreadcrumbList items={[
                { link: UserStore.getUser['user_role'] === "STUDENT" ? "/student/exams" : "/faculty/exams", label: "Exams" },
                ExamStore.getExam.exam && ExamStore.getExam.exam.exam_name ? ExamStore.getExam.exam.exam_name : <CircularProgress sx={{ color: "textSecondary" }} size={12} />
            ]} />}

            {fromSubject && <Button sx={{ textTransform: 'capitalize', marginTop: "-10px", marginBottom: "10px" }} startIcon={Icons.default.ArrowBack} onClick={() => navigate( `${UserStore.getUser['user_role'] === "STUDENT" ? `/student/subject/${SubjectStore.getSubject.subject_id}/exams` : `/faculty/subject/${SubjectStore.getSubject.subject_id}/exams`}` )} >Back to Exams</Button>}

            {!inProgress && ExamStore.getExam.exam && ExamStore.getExam.exam.exam_name && <>
                <Box display="flex" flexGrow={1} flexDirection="column" overflow="auto" gap="20px" >
                    <Box display="flex" gap={layout.cardsGap} alignItems="flex-start" >
                        <IconButton onClick={() => navigate( `/${UserStore.getUser.user_role === "STUDENT" ? "student" : "faculty"}/exams` )} size='small' sx={{ border: border[1], marginTop: "3px" }}>
                            {Icons.default.ArrowBack}
                        </IconButton>
                        <Box>
                            <Typography variant="h6" fontSize="16px" >{ExamStore.getExam.exam.exam_name}</Typography>
                            <Box display="flex" gap="5px" alignItems="center" marginTop="3px" width="100%" minWidth={0}>
                                {ExamStore.getExam?.exam?.subject?.subject_name && <CustomChip tooltipTitle={`Exam subject`} value={ExamStore.getExam.exam.subject?.subject_name} />}
                                {UserStore.getUser.user_role !== "STUDENT" && batches?.map( groupId => {
                                    const group = getTransformedGroupDetails( UserStore.getUsersLegacyAndCurrentBatchesAndSectionList?.find( i => ( +i.batch_assignment_id === +groupId || `${i.section_assignment_id}-${i.section_short_name}` === String( groupId ) ) ) )
                                    if ( !group ) {

                                        const entity = groupId.match( /^(\d+-[A-Z])$/ ) ? "Section" : "Batch"

                                        return <CustomChip key={groupId} tooltipTitle={`Assigned ${entity}`} value={`${entity}: ${groupId}`} />
                                    }
                                    return (
                                        <CustomChip tooltipTitle={`Assigned ${group?.entity}`} key={groupId} value={group.chip_label} />
                                    )
                                } )}
                            </Box>
                        </Box>
                    </Box>

                    <Box display="grid" gap={layout.cardsGap} width="100%" gridTemplateColumns={{ md: "repeat(4,1fr)", xs: "repeat(2,1fr)" }}>
                        {!published && <CountCard label='Status' color='error' icon={Icons.default.Pending} value={ExamStore.getExam.exam.exam_status} />}
                        {!started && <CountCard label='Starts at' icon={Icons.default.Login} value={formatDateTimeForDisplay( ExamStore.getExam.exam.exam_start_date, ExamStore.getExam.exam.exam_start_time )} />}
                        {!started && <CountCard label='Ends at' icon={Icons.default.Logout} value={formatDateTimeForDisplay( ExamStore.getExam.exam.exam_end_date, ExamStore.getExam.exam.exam_end_time )} />}
                        {ended && <CountCard label='Ended at' icon={Icons.default.Logout} value={formatDateTimeForDisplay( ExamStore.getExam.exam.exam_end_date, ExamStore.getExam.exam.exam_end_time )} />}
                        {started && <CountCard label='Maximum Attempts' icon={Icons.default.Replay} value={ExamStore.getExam.exam.maximum_attempts} />}
                        {ExamStore.getExam.exam.exam_duration && <CountCard label='Duration' icon={Icons.default.Clock} value={ExamStore.getExam.exam.exam_duration} />}
                        {published && UserStore.getUser.user_role !== "STUDENT" && <CountCard onClick={() => navigate( `/faculty/exams/${id}/participation` )} label='Attempts' icon={Icons.default.Group} value={attempts} />}
                        {UserStore.getUser.user_role === "STUDENT" && <CountCard label='Maximum Attempts' icon={Icons.default.Replay} value={ExamStore.getExam.exam.maximum_attempts} />}
                    </Box>

                    <Box display="flex" flexDirection="column" gap="10px">
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px", "@media(max-width:800px)": { flexDirection: "column", alignItems: "flex-start", flexWrap: "wrap" } }} >
                            {UserStore.getUser["user_role"] === 'STUDENT' && <Box display="flex" gap="10px" title={ended ? "Exam already ended" : ""}>
                                <Button size='small' sx={{ '&.Mui-disabled': { pointerEvents: "all", cursor: "not-allowed" } }} variant='contained' startIcon={attempting ? <CircularProgress size={14} /> : Icons.default.Checklist} disableElevation onClick={attempt} disabled={attempting || ended}>
                                    Attempt test
                                </Button>
                                {ended && !ExamStore.getExam?.exam?.exam_constraints?.hide_scores && <Button size='small' onClick={() => navigateOrOpen( `/student/scores/${id}` )} variant='contained' disableElevation>
                                    View scores
                                </Button>}
                            </Box>}

                            {UserStore.getUser["user_role"] !== 'STUDENT' && <Box flexWrap="wrap" display="flex" gap="10px" >
                                {!started && !published && <Button size='small' sx={{ '&.Mui-disabled': { pointerEvents: "all", cursor: "not-allowed" }, textTransform: "capitalize" }} disableElevation type="button" disabled={( started || isImporting || published )} startIcon={Icons.default.DownloadIcon} onClick={() => setImportDialogStatus( true )} color='primary' variant='contained'>Import Questions</Button>}
                                {!started && !published && <Button size='small' sx={{ '&.Mui-disabled': { pointerEvents: "all", cursor: "not-allowed" }, textTransform: "capitalize" }} disableElevation disabled={( started || published )} onClick={() => navigateOrOpen( `/faculty/exams/${ExamStore.getExam.exam.exam_id}/addquestions` )} color="customThemeColor" variant='contained' startIcon={Icons.default.AddIcon}>Add questions </Button>}
                                {ExamStore.getExam.exam.exam_status.toLowerCase() !== "published" && <Button size='small' startIcon={publishing ? <CircularProgress size={14} /> : Icons.default.Publish} disableElevation variant="contained" onClick={publish} color='secondary'>{publishing ? "Publishing..." : "Publish"}</Button>}
                                {UserStore.getUser["user_role"] !== 'STUDENT' && !started && !published && <Tooltip title={( ended || published ) ? "Exam Published or already started" : `Edit ${ExamStore.getExam?.exam?.exam_name}`}>
                                    <span>
                                        <Button size='small' sx={{ '&.Mui-disabled': { pointerEvents: "all", cursor: "not-allowed" }, textTransform: "capitalize" }} variant='outlined' disableElevation disabled={started || published} color="secondary" onClick={() => navigateOrOpen( `/faculty/exams/${ExamStore.getExam.exam.exam_id}/edit` )} startIcon={Icons.default.EditIconOutlined} >Edit</Button>
                                    </span>
                                </Tooltip>}
                                {ExamStore.getExam.exam.exam_status.toLowerCase() === "published" && <Button size='small' disableElevation variant='contained' startIcon={Icons.default.TrendingUp} onClick={() => navigateOrOpen( `/faculty/exams/${ExamStore.getExam.exam.exam_id}/participation` )}>View Scores</Button>}
                                {UserStore.getUser["user_role"] !== 'STUDENT' && <Button size='small' disableElevation variant='outlined' startIcon={Icons.default.DeleteIconOutlined} onClick={() => setDeleteDialogState( true )} color='error'>Delete </Button>}
                            </Box>}

                        </Box>
                        {UserStore.getUser["user_role"] !== 'STUDENT' && !started && !published && <Box display="flex" gap="5px" width="fit-content" alignItems="center" padding="5px 10px" color={palette.text.primary} border={`1px solid ${palette.primary.dark}`} bgcolor={palette.primary.light + "11"} borderRadius="5px" fontWeight="300" fontSize="12px">
                            <Box display="flex" height="100%" alignItems="center" component="span" fontSize="1.2em" >
                                {Icons.default.Info}
                            </Box>
                            <i>To know about the import file format <Typography color='primary' display="inline" component="span" fontSize="12px" sx={{ textDecoration: "underline" }} >
                                <Link to='https://docs.moodle.org/401/en/Aiken_format' className='default-link' target="_blank" >click here.
                                </Link>
                            </Typography>
                            </i>
                        </Box>}
                    </Box>


                    <Typography textAlign="justify" variant="body2">
                        {ExamStore.getExam.exam.exam_description}
                    </Typography>

                    {UserStore.getUser.user_role === "STUDENT" && <Box>
                        <Typography component="h2" variant='subtitle2' fontSize="12px" gutterBottom >Topics</Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", }} flexWrap="wrap" gap="5px">
                            {ExamStore.getExam.exam.exam_topics && ExamStore.getExam.exam.exam_topics.map( ( topic, index ) => (
                                <Chip key={topic} label={topic} sx={{ fontSize: "12px", marginRight: "5px", borderRadius: "5px", border: border[1], bgcolor: palette.contentBg, fontWeight: "600" }} />
                            ) )}
                        </Box>
                    </Box>}

                    {UserStore.getUser.user_role !== 'STUDENT' && <Box flexGrow={1} border={border[1]} borderRadius="5px" display={{ md: "flex", xs: "block" }} flexDirection="column" minHeight={0} overflow="auto" >
                        <CustomTabs tabs={facultyTabs} variant='stretched' handleTabChange={setTabValue} value={tabValue} styles={{ width: "initial" }} />

                        {tabValue === 'topicsandbatches' && <Box display="flex" flexDirection="column" padding="20px">
                            <Box>
                                <Typography gutterBottom variant='h6' fontSize="14px" color={palette.shadedText}>Exam for batch(es):</Typography>
                                <Box display="flex" gap="10px" flexWrap="wrap" alignItems="center">
                                    {batches && batches.map( ( batch, index ) => {

                                        const access = UserStore.getUsersLegacyAndCurrentBatchesAndSectionList?.find( i => ( +i.batch_assignment_id === +batch || `${i.section_assignment_id}-${i.section_short_name}` === String( batch ) ) )

                                        if ( !access ) {
                                            const entity = batch.match( /^(\d+-[A-Z])$/ ) ? "Section" : "Batch"
                                            return <CustomChip key={batch} tooltipTitle={`Assigned ${entity}`} value={`${entity}: ${batch}`} />
                                        }

                                        let title = ''
                                        if ( access?.section_assignment_id ) {
                                            title = <Box key={batch} sx={{ padding: "5px 10px", marginRight: "5px", borderRadius: "5px", border: border[1], bgcolor: palette.contentBg, fontWeight: "600" }} >
                                                <Typography fontSize="12px" fontWeight="inherit">{`${access.course_branch_short_name ? access.course_branch_short_name?.trim() + "," : ""} ${access?.current_year ? `year: ${access?.current_year},` : ""} ${access.current_sem ? `Sem: ${access.current_sem},` : ""} Sec: ${access.section_short_name}`}</Typography>
                                                <Typography fontSize="10px" fontWeight="inherit">{access.ac_year}</Typography>
                                            </Box>
                                        } else if ( access?.batch_assignment_id )
                                            title = <Box key={batch} sx={{ padding: "5px 10px", marginRight: "5px", borderRadius: "5px", border: border[1], bgcolor: palette.contentBg, fontWeight: "600" }} >
                                                <Typography fontSize="12px" fontWeight="inherit">{`${access.course_name_short ? access.course_name_short + "," : ""} ${access?.current_year ? `year: ${access?.current_year},` : ""} ${access.current_sem ? `Sem: ${access.current_sem},` : ""} Batch: ${access.batch_short_name}`}</Typography>
                                                <Typography fontSize="10px" fontWeight="inherit">{access.ac_year}</Typography>
                                            </Box>
                                        return (
                                            title
                                        )
                                    } )}
                                    {( !batches || batches.length === 0 ) && <Typography variant='body2' color="textSecondary">Exam is not yet assigned to batches!</Typography>}
                                </Box>
                            </Box>
                        </Box>}
                        {tabValue === 'questions' && <Box flexGrow={1} overflow="auto" className='slim-custom-scrollbar' padding="20px">
                            <Typography component="h2" fontSize="12px" gutterBottom >Topics</Typography>
                            <Box sx={{ display: "flex", flexWrap: "wrap", }} flexWrap="wrap" gap="5px">
                                {ExamStore.getExam.exam.exam_topics && ExamStore.getExam.exam.exam_topics.map( ( topic, index ) => (
                                    <Chip key={topic} label={topic} sx={{ fontSize: "12px", marginRight: "5px", borderRadius: "5px", border: border[1], bgcolor: palette.contentBg, fontWeight: "600" }} />
                                ) )}
                            </Box>

                            <Typography variant='h6' component="h2" marginTop={layout.sectionsGap} fontSize="12px" gutterBottom >Questions</Typography>
                            {questions && questions.length > 0 && <Box display="flex" flexDirection="column" gap="10px">
                                {questions.map( ( question, index ) => {
                                    return (
                                        <Question editable={!( new Date( formatDateTimeForInputField( ExamStore.getExam.exam.exam_start_date, ExamStore.getExam.exam.exam_start_time ) ) < new Date() || ExamStore.getExam.exam.exam_status === 'Published' )} fetchQuestions={getQuestions} index={index} question={question.question} key={question.question.question_id} setQuestionSelectedForEdit={setQuestionSelectedForEdit} />
                                    )
                                } )}
                            </Box>}
                            {( !questions || questions.length === 0 ) && <Typography color="textSecondary" marginTop="10px" variant='body2'>Questions not yet added!</Typography>}
                        </Box>}
                    </Box>}

                </Box>
            </>
            }
            {inProgress &&
                <Box height="100%">
                    <DataLoadingSpinner padding="0" waitingMessage="Loading exam..." />
                </Box>
            }
        </Box> :
            <Box flexGrow={1} display="flex" gap="20px" flexDirection="column" padding="20px" >
                <Box sx={{ flexGrow: 1, display: "flex", gap: "20px", flexDirection: "column" }}>
                    <Box display="flex" gap="20px" flexDirection="column" alignItems="center" justifyContent="center" flexGrow={1} padding="20px">
                        <img width="180px" src={examNotFoundImg} alt="Exam not found" />
                        <Typography color="error.main" variant="h6">404 ERROR</Typography>
                        <Typography variant="subtitle2">Ooops! The exam you are looking for does not exists any more!</Typography>
                        <Button onClick={navigateToExamsPage} startIcon={Icons.default.ArrowBack} sx={{ textTransform: "capitalize" }} variant="outlined" color="error">Back to exams</Button>
                    </Box>
                </Box>
            </Box>
    )
} )

export default Exam